import i18n from "@/i18n";

export type Era = '1920s' | 'gaslight' | 'modern' | 'regency' | 'western' | 'darkages' | 'invictus' | 'pulp';

interface EraPlaceholders {
  details: {
    name: string;
    age: string;
    residence: string;
    birthplace: string;
  };
  backstory: {
    personalDescription: string;
    traits: string;
    ideology: string;
    significantPeople: string;
    meaningfulLocations: string;
    treasuredPossessions: string;
    injuriesScars: string;
    phobiasManias: string;
    encountersWithStrangeEntities: string;
  };
}

const VALID_ERAS: Era[] = ['1920s', 'gaslight', 'modern', 'regency', 'western', 'darkages', 'invictus', 'pulp'];

const BACKSTORY_FIELDS = [
  'personalDescription',
  'traits',
  'ideology',
  'significantPeople',
  'meaningfulLocations',
  'treasuredPossessions',
  'injuriesScars',
  'phobiasManias',
  'encountersWithStrangeEntities',
] as const;

const DETAIL_FIELDS = ['name', 'age', 'residence', 'birthplace'] as const;

function buildPlaceholders(era: Era): EraPlaceholders {
  const details = Object.fromEntries(
    DETAIL_FIELDS.map((f) => [f, i18n.t(`details.eraPlaceholders.${era}.${f}`, { ns: 'widgets' })])
  ) as EraPlaceholders['details'];
  const backstory = Object.fromEntries(
    BACKSTORY_FIELDS.map((f) => [f, i18n.t(`backstoryPlaceholders.${era}.${f}`, { ns: 'flow' })])
  ) as EraPlaceholders['backstory'];
  return { details, backstory };
}

/**
 * Returns era-appropriate placeholder examples in the active language.
 * Note: the result reflects the current i18n language at call time. Components
 * should re-render on language change (most consumers do via useTranslation()).
 */
export function getEraPlaceholders(era: string): EraPlaceholders {
  const key = (era || '').toLowerCase() as Era;
  const resolved = VALID_ERAS.includes(key) ? key : '1920s';
  return buildPlaceholders(resolved);
}

// Legacy export retained for any downstream consumers; built lazily via getter.
export const ERA_PLACEHOLDERS = new Proxy({} as Record<Era, EraPlaceholders>, {
  get: (_t, prop: string) => buildPlaceholders((VALID_ERAS.includes(prop as Era) ? prop : '1920s') as Era),
}) as Record<Era, EraPlaceholders>;
